import * as React from 'react'
import styled, { css } from 'styled-components'
import { ColorName, Colors } from '../constants'
import { TourType, ConversationType, LeasePropertyType, TaskType, UserType } from '../types.gen'

const iconCodes = {
  'task-generic': '\uEA02',
  'task-firm': '\uEA03',
  'task-multi-family': '\uEA04',
  'task-single-family': '\uEA05',
  'task-student-housing': '\uEA13',
  'task-senior-housing': '\uEA10',
  'task-construction': '\uEA23',
  'channel-sms': '\uEA06',
  'channel-email': '\uEA07',
  'contact-boss': '\uEA08',
  'thread-editing': '\uEA09',
  'contact-agent': '\uEA0A',
  'contact-prospect-offered': '\uEA0B',
  'contact-prospect-accepted': '\uEA0C',
  'contact-prospect-confirmed': '\uEA0D',
  'message-call-missed': '\uEA14',
  'message-call-connected': '\uEA15',
  'message-experiment': '\uEA27',
  'message-reply': '\uEA28',
  'contact-help': '\uEA1B',
  'contact-investor': '\uEA1C',
  'user-operator': '\uEA1D',
  'user-senior-operator': '\uEA22',
  'time-day': '\uEA25',
  'time-night': '\uEA26',
  close: '\uEA29',
  'tour-type-virtual': '\uea2a',
  'tour-type-in-person': '\uea32',
  'tour-type-self-guided': '\uea43',
  floorplan: '\uea44',
  check: '\uea45',
  'user-team-lead': '\uea48',
  'user-admin': '\uea49',
  'logo-github': '\uea4e',
  'logo-slack': '\uea4f',
  'contact-language': '\uea51',
  'task-onboard': '\uea52',
  'task-offboard': '\uea53',
  'task-merge': '\uea54',
  'logo-pagerduty': '\uea55',
}

export type IconName = keyof typeof iconCodes

export type IconSize = 'small' | 'medium' | 'large'

export default class Icon extends React.PureComponent<{
  name: IconName
  size?: IconSize
  color?: ColorName
  title?: string
}> {
  render() {
    const { size, name, color, title } = this.props
    return (
      <IconText size={size || 'small'} color={color} title={title}>
        {iconCodes[name]}
      </IconText>
    )
  }
}

function sizeToPixels(size: IconSize) {
  switch (size) {
    case 'small':
      return '12px'

    case 'medium':
      return '16px'

    case 'large':
      return '24px'

    default:
      throw new Error('unknown icon size: ' + size)
  }
}

/**
 * Takes a LeasingTask.tourType and returns matching `Icon`
 */
export function tourTypeToIcon(tourType: TourType, color?: ColorName, size?: IconSize) {
  const iconName = tourTypeToIconName(tourType)
  if (!iconName) {
    return null
  }
  return <Icon name={iconName} color={color} size={size} />
}

export function tourTypeToIconName(tourType: TourType): IconName | undefined {
  switch (tourType) {
    case TourType.SELF_GUIDED:
      return 'tour-type-self-guided'
    case TourType.VIRTUAL:
      return 'tour-type-virtual'
    case TourType.IN_PERSON:
      return 'tour-type-in-person'
    default:
      return undefined
  }
}

export function tourTypeToColor(tourType: TourType): ColorName {
  switch (tourType) {
    case TourType.SELF_GUIDED:
      return Colors.mint as ColorName
    case TourType.VIRTUAL:
      return Colors.sky as ColorName
    case TourType.IN_PERSON: // skip fancy color b/c case is so common
    default:
      return Colors.silver as ColorName
  }
}

// convenience method for `InboxSidebar`, `TaskLinks`, etc
// takes a conversationType and returns the right `Icon`
export function convoTypeToIcon(cType: ConversationType) {
  const iconName = convoTypeToIconName(cType)
  if (!iconName) {
    return null
  }
  return <Icon name={iconName} />
}

export function convoTypeToIconName(cType: ConversationType): IconName | undefined {
  switch (cType) {
    case ConversationType.AGENT:
      return 'contact-agent'
    case ConversationType.BOSS:
      return 'contact-boss'
    case ConversationType.INVESTOR:
      return 'contact-investor'
    case ConversationType.OPERATOR:
      return 'user-operator'
    default:
      return undefined
  }
}

/**
 * Accepts a UserType (OPERATOR, TEAM_LEAD, etc) and returns the matching Icon.
 *
 * Convenience method for `UserEditBoxes` in Admin section.
 */
export function userTypeToIcon(userType: UserType) {
  const iconName = userTypeToIconName(userType)
  if (!iconName) {
    return null
  }
  return <Icon name={iconName} />
}

export function userTypeToIconName(userType: UserType): IconName | undefined {
  switch (userType) {
    case UserType.ENGINEER:
    case UserType.OPS_MANAGER:
    case UserType.ANALYST:
      return 'user-admin'
    case UserType.DISABLED:
      return 'close'
    case UserType.OPERATOR:
      return 'user-operator'
    case UserType.SENIOR_OPERATOR:
      return 'user-senior-operator'
    case UserType.TEAM_LEAD:
      return 'user-team-lead'
    default:
      return undefined
  }
}

export function propertyTypeToIconName(type: LeasePropertyType | TaskType.LEASING_FIRM): IconName {
  switch (type) {
    case LeasePropertyType.SFR:
      return 'task-single-family'
    case LeasePropertyType.MF:
      return 'task-multi-family'
    case LeasePropertyType.STUDENT:
      return 'task-student-housing'
    case LeasePropertyType.SENIOR:
      return 'task-senior-housing'
    case LeasePropertyType.CONSTRUCTION:
      return 'task-construction'
    case TaskType.LEASING_FIRM:
      return 'task-firm'
    default:
      return 'task-generic'
  }
}

/**
 * Given a time and timezone, return a "day" or "night" icon
 */
export function timeToIconName(d: Date, tz: string): IconName {
  // First, gets the "hour" value as a Number from a Date, respecting the timeZone
  // as odd as this looks, toLocaleTimeString() is the only method returning hours that respects tz
  // if you don't localize the time like this, the icon would show the Operator's tz, not the task tz
  const hourOfDay = +d.toLocaleTimeString('en-US', { hour: 'numeric', timeZone: tz, hour12: false }).substring(0, 2)

  if (hourOfDay > 6 && hourOfDay < 20) {
    return 'time-day'
  } else {
    return 'time-night'
  }
}

const IconText = styled.div`
  ${(props: { size?: IconSize; color?: ColorName; title?: string }) => css`
    display: inline-block;
    position: relative;
    top: 1px;
    font-family: 'dynasty-icon';
    font-size: ${sizeToPixels(props.size || 'small')};
    ${props.color ? 'color: ' + Colors[props.color] : ''};
    user-select: none;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -o-user-select: none;
    ${props.title ? 'opacity: 0.88;' : ''};

    &:hover {
      ${props.title ? 'opacity: 1;' : ''};
    }
  `};
`
